import {
  SetterOrUpdater,
  atom,
  useRecoilState,
  useResetRecoilState,
} from 'recoil';
import { useCallback, useMemo } from 'react';
import { merge } from 'es-toolkit/compat';
import { AuthState, DeepPartial, User } from 'shared/types';
import { ReportableGeoEventType } from 'features/geoEvents/geoEvent.types';
import { ENV } from 'shared/ENV';
import { permanentStorageEffect, localStorageEffect } from 'libs/recoil';
import {
  canUserReport,
  canUserReportFlood,
  canUserReportHazard,
  canViewFlightTrackerLayer,
  hasActiveMembership as userHasActiveMembership,
  hasMembershipBenefits,
  isInMembershipBenefitGroup,
  isIframe,
  isInternalUser,
  isLocationEditor,
  userInGroups,
} from 'shared/utils';
import {
  getValueForNoCacheHeader,
  setUseNoCacheHeader,
} from 'shared/noCacheUtil';
import { useAuthUserQuery } from 'hooks/useAuthUserQuery';
import {
  persistedQueryClient,
  unpersistedQueryClient,
} from 'components/CacheableQueryClientProvider';
import posthog from 'posthog-js';
import {
  clearDataForLogout,
  LOCAL_STORAGE_KEY,
  SESSION_STORAGE_ACCESSKEY_KEY,
} from '../localStorage';

export type PermissionData = {
  canModerate: boolean;
  canReportWildfire: boolean;
  canReportFlooding: boolean;
  canReportHazard: boolean;
  isRxOnlyReporter: boolean;
  isInternalUser: boolean;
  isFloodStaffReporter: boolean;
  isWildfireStaffReporter: boolean;
  canCreateLocation: boolean;
  canManageOrganization: boolean;
  canSendCriticalAlerts: boolean;
  canViewInternalLightningLayer: boolean;
  canViewFlightTrackerLayer: boolean;
  isApprover: boolean;
};

// Maps a reportable geo event type to its corresponding report permission flag.
export const canReportGeoEventType = (
  permissions: Partial<PermissionData>,
  geoEventType: ReportableGeoEventType,
): boolean => {
  const canReportByGeoEventType: Record<ReportableGeoEventType, boolean> = {
    wildfire: Boolean(permissions.canReportWildfire),
    flooding: Boolean(permissions.canReportFlooding),
    hazard: Boolean(permissions.canReportHazard),
  };
  return canReportByGeoEventType[geoEventType];
};

type UseAuthState = {
  isAuthenticated: boolean;
  user: User | null;
  hasActiveMembership: boolean;
  showMembershipFeatures: boolean;
  showMembershipProFeatures: boolean;
  isSupporter: boolean;
  permissions: PermissionData;
  loginSuccess: (key: string, userData: User) => void;
  setAuth: SetterOrUpdater<AuthState>;
  logout: () => Promise<void>;
  updateAuth: (obj: DeepPartial<AuthState>) => void;
  isProMember: boolean;
  showSupportUs: boolean;
  isOrganizationUser: boolean;
};

export const getDefaultAuthState = (): AuthState => ({
  isAuthenticated: false,
  accessKey: null,
});

export const authState = atom({
  key: 'AUTH_STATE',
  default: getDefaultAuthState(),
  effects: [
    ENV.VITE_USE_NEW_STORAGE
      ? permanentStorageEffect('AUTH_STATE')
      : localStorageEffect(LOCAL_STORAGE_KEY.AUTH_STATE),
  ],
});

const getPermissions = (user: User | null): PermissionData => {
  const canModerate = userInGroups(user, [
    'wildfire_staff_reporters',
    'reporters',
  ]);
  const canReportWildfire = canUserReport(user);
  const canReportFlooding = canUserReportFlood(user);
  const canReportHazard = canUserReportHazard(user);
  const canCreateLocation =
    userInGroups(user, ['wildfire_staff_reporters', 'reporters']) ||
    canReportFlooding ||
    isLocationEditor(user);
  const isRxOnlyReporter = userInGroups(user, ['rx_reporters']);
  const isFloodStaffReporter = userInGroups(user, ['flood_staff_reporters']);
  const isWildfireStaffReporter = userInGroups(user, [
    'wildfire_staff_reporters',
  ]);
  const canManageOrganization = Boolean(
    user?.supporterStatus?.canManageOrganization,
  );
  const canSendCriticalAlerts = userInGroups(user, ['send_critical_alerts']);
  const canViewInternalLightningLayer =
    user?.permissions?.canViewInternalLightningLayer === 'always';
  const isApprover = userInGroups(user, ['approvers']);

  return {
    canModerate,
    canReportWildfire,
    canReportFlooding,
    canReportHazard,
    isRxOnlyReporter,
    isInternalUser: isInternalUser(user),
    isFloodStaffReporter,
    isWildfireStaffReporter,
    canCreateLocation,
    canManageOrganization,
    canSendCriticalAlerts,
    canViewInternalLightningLayer,
    canViewFlightTrackerLayer: canViewFlightTrackerLayer(user),
    isApprover,
  };
};

export const useAuthState = (): UseAuthState => {
  const resetAuthState = useResetRecoilState(authState);
  const [auth, setAuth] = useRecoilState(authState);

  const authUser = useAuthUserQuery(auth.isAuthenticated);

  const updateAuth = useCallback(
    (obj: DeepPartial<AuthState>) => {
      setAuth((prev) => merge({}, prev, obj));
    },
    [setAuth],
  );

  const loginSuccess = useCallback(
    (key: string, userData: User) => {
      updateAuth({ isAuthenticated: true, accessKey: key });
      persistedQueryClient.setQueryData(['auth-user'], userData);
      unpersistedQueryClient.removeQueries({ queryKey: ['permissions'] });

      // Set "useNoCacheHeader" based on user's permissions (true for internal users, and reporters)
      // This ensures immediate updates when a user logs in, avoiding delays from:
      // 1. Stale values persisting in sessionStorage
      // 2. Slow propagation of Recoil state updates
      setUseNoCacheHeader(getValueForNoCacheHeader(userData));

      // Make access token immediately available to getAccessKey() since Recoil updates AUTH_STATE to disk slowly
      sessionStorage.setItem(SESSION_STORAGE_ACCESSKEY_KEY, key);
    },
    [updateAuth],
  );

  const logout = useCallback(async () => {
    await clearDataForLogout();
    resetAuthState();
    posthog.reset();
    window.location.reload();
  }, [resetAuthState]);

  const hasActiveMembership = userHasActiveMembership(authUser);

  const isProMember =
    hasActiveMembership && authUser?.supporterStatus?.planType === 'pro';

  const showMembershipFeatures = hasMembershipBenefits(authUser);

  const showMembershipProFeatures =
    isProMember || isInMembershipBenefitGroup(authUser);

  const isSupporter = useMemo(
    (): boolean =>
      hasActiveMembership ||
      authUser?.supporterStatus?.isDonor ||
      isInMembershipBenefitGroup(authUser),
    [authUser, hasActiveMembership],
  );

  const permissions = useMemo(() => getPermissions(authUser), [authUser]);

  const isOrganizationUser = Boolean(
    authUser?.supporterStatus?.isOrganizationUser,
  );

  const showSupportUs =
    !permissions.isInternalUser && !isOrganizationUser && !isIframe();

  return {
    isAuthenticated: auth.isAuthenticated,
    user: authUser,
    hasActiveMembership,
    isSupporter,
    showMembershipFeatures,
    showMembershipProFeatures,
    permissions,
    loginSuccess,
    setAuth,
    logout,
    updateAuth,
    isProMember,
    showSupportUs,
    isOrganizationUser,
  };
};
